import { lazy, Suspense, useEffect } from 'react'

import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useI18n } from '@/presentation/hooks/useI18n'
import { FuelFormModal } from '@/presentation/modules/Base/components/FuelForms/FuelFormModal'
import { RegisterProviderForm } from '@/presentation/modules/Base/components/FuelForms/RegisterProviderForm'
import { CenteredSpinner } from '@/presentation/modules/Base/components/Spinner'
import {
  FuelFormState,
  FuelFormStateMutator,
  FuelFormStateProps
} from '@/presentation/modules/MapScreen/states/FuelFormState'

/**
 * `RegisterPriceForm` e a unica porta de entrada de `@mui/x-date-pickers` (0,57 MB) fora das rotas do
 * drawer, e este presenter e montado eager em `App.tsx`. Carregar sob demanda tira a lib do chunk
 * inicial: o formulario so existe depois que o usuario abre o modal de preco.
 */
const RegisterPriceForm = lazy(() =>
  import('@/presentation/modules/Base/components/FuelForms/RegisterPriceForm').then((m) => ({
    default: m.RegisterPriceForm
  }))
)

function FuelFormPresenter() {
  const fuelFormState = useBehaviorSubject<FuelFormStateProps>(FuelFormState)
  const { t } = useI18n()
  const handleClose = () => FuelFormStateMutator.hide()

  useEffect(() => {
    if (!fuelFormState.active) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [fuelFormState.active])

  if (!fuelFormState.active) return null

  if (fuelFormState.active.type === 'register-price') {
    const { adhpCode, fuelType, providerOptions } = fuelFormState.active
    const fuelTypeLabel = fuelType === 'avgas' ? t('TOOLBAR_FUEL_ITEM_AVGAS') : t('TOOLBAR_FUEL_ITEM_JET-A')

    return (
      <FuelFormModal
        title={t('FUEL_MODAL_REGISTER-PRICE_TITLE', { adhpCode })}
        description={t('FUEL_MODAL_REGISTER-PRICE_SUBTITLE', { fuelType: fuelTypeLabel, adhpCode } as any)}
        onClose={handleClose}
      >
        <Suspense fallback={<CenteredSpinner />}>
          <RegisterPriceForm
            adhpCode={adhpCode}
            fuelType={fuelType}
            providerOptions={providerOptions}
            onCancel={handleClose}
            onSuccess={handleClose}
          />
        </Suspense>
      </FuelFormModal>
    )
  }

  if (fuelFormState.active.type === 'register-provider') {
    const { source, adhpCode, initialLayer } = fuelFormState.active
    const modalDescription =
      source === 'fuel-feature-info' && adhpCode
        ? t('FUEL_MODAL_REGISTER-PROVIDER_SUBTITLE_WITH_ADHP', { adhpCode })
        : t('TOOLBAR_FUEL_ITEM_REGISTER-SUPPLIER_MODAL_SUBTITLE')

    return (
      <FuelFormModal
        title={t('TOOLBAR_FUEL_ITEM_REGISTER-SUPPLIER')}
        description={modalDescription}
        onClose={handleClose}
      >
        <RegisterProviderForm
          source={source}
          adhpCode={adhpCode}
          initialLayer={initialLayer}
          mode="register"
          onCancel={handleClose}
          onSuccess={handleClose}
        />
      </FuelFormModal>
    )
  }

  if (fuelFormState.active.type === 'edit-provider') {
    const { adhpCode, provider, initialLayer } = fuelFormState.active

    return (
      <FuelFormModal
        title={t('FUEL_MODAL_EDIT-PROVIDER_SUBTITLE', { adhpCode })}
        description={t('FUEL_MODAL_REGISTER-PROVIDER_SUBTITLE_WITH_ADHP', { adhpCode })}
        onClose={handleClose}
      >
        <RegisterProviderForm
          source="fuel-feature-info"
          mode="edit"
          adhpCode={adhpCode}
          initialProvider={provider}
          initialLayer={initialLayer}
          onCancel={handleClose}
          onSuccess={handleClose}
        />
      </FuelFormModal>
    )
  }

  return null
}

export { FuelFormPresenter }
